import * as Yup from 'yup';
import { isFuture, isToday } from 'date-fns';
import { trimCreditCardNumber } from '@shared/commonFunctions';
import {
  AMERICAN_EXPRESS_CREDIT_CARD_CVV_LENGTH,
  CVV_LENGTH,
  CARD_DIGIT_LENGTH,
  AMERICAN_EXPRESS_CREDIT_CARD_DIGIT_LENGTH,
} from '@shared/constants/constants';
import { AmericanExpress } from '@shared/fields/CvvInputField';
import { CreditCardTypeEnum } from './types';

const DISCOVERY_CARD_STARTS_WITH = '6';
const AMEX_CARD_STARTS_WITH = ['34', '37'];

const cardNumberValidation = Yup.string()
  .required()
  .nullable()
  .test('Credit Card Length', 'Card number is required', (value, context) => {
    if (value) {
      const trimValue = trimCreditCardNumber(value);
      const valueLength = trimValue.length;
      const { creditCardType } = context.parent;

      if (!creditCardType && valueLength !== CARD_DIGIT_LENGTH) {
        return context.createError({
          message: `Card Number must be at least ${CARD_DIGIT_LENGTH} character`,
        });
      }

      if (creditCardType && creditCardType === AmericanExpress && valueLength !== AMERICAN_EXPRESS_CREDIT_CARD_DIGIT_LENGTH) {
        return context.createError({
          message: `Card Number must be at least ${AMERICAN_EXPRESS_CREDIT_CARD_DIGIT_LENGTH} character`,
        });
      }

      if (
        creditCardType &&
        creditCardType === AmericanExpress &&
        valueLength === AMERICAN_EXPRESS_CREDIT_CARD_DIGIT_LENGTH &&
        // Amex Credit Card should starts with 34 or 37
        !AMEX_CARD_STARTS_WITH.includes(trimValue.slice(0, 2))
      ) {
        return context.createError({
          message: 'Invalid credit card number',
        });
      }

      if (
        creditCardType &&
        creditCardType === CreditCardTypeEnum.Discover &&
        valueLength === CARD_DIGIT_LENGTH &&
        // Discover Credit Card should starts with 6
        !trimValue.startsWith(DISCOVERY_CARD_STARTS_WITH)
      ) {
        return context.createError({
          message: 'Invalid credit card number',
        });
      }

      if (creditCardType && creditCardType !== AmericanExpress && valueLength !== CARD_DIGIT_LENGTH) {
        return context.createError({
          message: `Card Number must be at least ${CARD_DIGIT_LENGTH} character`,
        });
      }

      return true;
    }

    return false;
  });

const cvvValidation = Yup.string()
  .required()
  .nullable()
  .test('Security Code checker', 'Security code is required', (value, context) => {
    if (value) {
      const valueLength = value.length;
      const { creditCardType } = context.parent;

      if (!creditCardType && valueLength < CVV_LENGTH) {
        return context.createError({
          message: `Card Number must be at least ${CVV_LENGTH} character`,
        });
      }

      if (creditCardType && creditCardType === AmericanExpress && valueLength !== AMERICAN_EXPRESS_CREDIT_CARD_CVV_LENGTH) {
        return context.createError({
          message: `Security code should has ${AMERICAN_EXPRESS_CREDIT_CARD_CVV_LENGTH} digits`,
        });
      }

      if (creditCardType && creditCardType !== AmericanExpress && valueLength !== CVV_LENGTH) {
        return context.createError({
          message: `Security code should has ${CVV_LENGTH} digits`,
        });
      }

      return true;
    }

    return false;
  });

export const savePaymentValidation = (isSaved: boolean) =>
  Yup.object().shape({
    holderName: Yup.string().required(),
    processingDate: Yup.string()
      .nullable()
      .required('Processing Date is required')
      .test('ProcessingDateChecker', 'Processing Date should be today or future date', (value) => {
        if (value && (isToday(new Date(value)) || isFuture(new Date(value)))) {
          return true;
        }

        return false;
      }),
    expiryDate: Yup.string().required().typeError('Expiry Date is required'),
    amount: Yup.number().nullable().min(1, 'Amount must be greater than 0'),
    creditCardType: Yup.string().nullable().required(),
    paymentType: Yup.string().nullable(),
    notes: Yup.string().nullable(),
    cardNumber: isSaved ? Yup.string() : cardNumberValidation,
    cvv: isSaved ? Yup.string() : cvvValidation,
  },[['paymentType', 'amount']]);

export const updateCardValidationSchema = Yup.object().shape({
  holderName: Yup.string().required(),
  expiryDate: Yup.string().required().typeError('Expiry Date is required'),
  creditCardType: Yup.string().required().nullable(),
  cardNumber: cardNumberValidation,
  cvv: cvvValidation,
});
